import { Link } from 'react-router-dom';
import { Car } from '../../types/Car';

interface Props {
  car: Car;
}

export default function CarCard({ car }: Props) {
  const price =
    typeof car.veh_price === 'number'
      ? car.veh_price.toLocaleString('en-IN')
      : car.veh_price;

  return (
    <>
      <Link to={`/car/${car.id}`} className="car-card" style={cardBase}>

        {/* IMAGE */}
        <div style={imgWrap}>
          <img
            src={car.img[0]}
            alt={car.name}
            loading="lazy"
            className="car-card-img"
            style={imgStyle}
          />

          {/* YEAR BADGE */}
          <span style={yearBadge}>{car.year}</span>

          {/* OWNERSHIP BADGE */}
          {car.ownership && (
            <span style={ownerBadge}>{car.ownership} Owner</span>
          )}

          {/* HOVER OVERLAY */}
          <div className="card-overlay" style={overlay} />
        </div>

        {/* BODY */}
        <div style={body}>

          {/* NAME */}
          <div style={nameStyle}>{car.name}</div>

          {/* KMS + YEAR */}
          <div style={metaRow}>
            <span style={metaDot}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              {car.kmsDriven} km
            </span>
            <span style={metaDivider} />
            <span style={metaDot}>{car.year}</span>
          </div>

          {/* SPEC PILLS */}
          <div style={pillRow}>
            <span style={pill}>{car.fuel}</span>
            <span style={pill}>{car.transmission}</span>
            {car.veh_engine_capacity && (
              <span style={pill}>{car.veh_engine_capacity}</span>
            )}
          </div>

          {/* FOOTER */}
          <div style={footer}>
            <div>
              <div style={priceLabel}>ASKING PRICE</div>
              <div style={priceValue}>₹ {price}</div>
            </div>
            <div className="view-btn" style={viewBtn}>
              View <span style={{ fontSize: '16px', lineHeight: 1 }}>→</span>
            </div>
          </div>
        </div>
      </Link>

      <style>{`
        .car-card {
          transition: transform 0.22s ease, box-shadow 0.22s ease;
        }
        .car-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 40px rgba(0,0,0,0.55) !important;
        }
        .car-card:hover .car-card-img {
          transform: scale(1.04);
        }
        .car-card:hover .card-overlay {
          opacity: 1 !important;
        }
        .car-card:hover .view-btn {
          background: #FFD700 !important;
          color: #000 !important;
        }
      `}</style>
    </>
  );
}

/* ── STYLES ── */

const cardBase: React.CSSProperties = {
  display: 'block',
  width: '100%',
  borderRadius: '14px',
  overflow: 'hidden',
  background: '#111',
  border: '1px solid #1e1e1e',
  textDecoration: 'none',
  color: 'inherit',
  boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
};

const imgWrap: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '185px',
  overflow: 'hidden',
  background: '#0d0d0d',
};

const imgStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
  transition: 'transform 0.4s ease',
};

const overlay: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 50%)',
  opacity: 0.6,
  transition: 'opacity 0.3s ease',
  pointerEvents: 'none',
};

const yearBadge: React.CSSProperties = {
  position: 'absolute',
  top: '10px',
  left: '10px',
  background: '#FFD700',
  color: '#000',
  fontSize: '11px',
  fontWeight: 700,
  padding: '3px 9px',
  borderRadius: '5px',
  letterSpacing: '0.04em',
};

const ownerBadge: React.CSSProperties = {
  position: 'absolute',
  bottom: '10px',
  left: '10px',
  background: 'rgba(0,0,0,0.72)',
  color: '#ccc',
  fontSize: '11px',
  padding: '3px 9px',
  borderRadius: '5px',
  border: '1px solid rgba(255,255,255,0.08)',
  backdropFilter: 'blur(4px)',
};

const body: React.CSSProperties = {
  padding: '14px 16px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const nameStyle: React.CSSProperties = {
  fontSize: '15px',
  fontWeight: 700,
  color: '#f0f0f0',
  lineHeight: 1.3,
  letterSpacing: '-0.01em',
};

const metaRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '12px',
  color: '#666',
};

const metaDot: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
};

const metaDivider: React.CSSProperties = {
  width: '3px',
  height: '3px',
  borderRadius: '50%',
  background: '#333',
  flexShrink: 0,
};

const pillRow: React.CSSProperties = {
  display: 'flex',
  gap: '6px',
  flexWrap: 'wrap' as const,
};

const pill: React.CSSProperties = {
  fontSize: '11px',
  background: '#1a1a1a',
  border: '1px solid #272727',
  color: '#999',
  padding: '3px 9px',
  borderRadius: '20px',
  letterSpacing: '0.02em',
};

const footer: React.CSSProperties = {
  marginTop: '4px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  borderTop: '1px solid #1a1a1a',
  paddingTop: '12px',
};

const priceLabel: React.CSSProperties = {
  fontSize: '9px',
  color: '#555',
  letterSpacing: '0.08em',
  marginBottom: '3px',
};

const priceValue: React.CSSProperties = {
  fontSize: '18px',
  fontWeight: 700,
  color: '#FFD700',
  letterSpacing: '-0.02em',
};

const viewBtn: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  fontSize: '12px',
  fontWeight: 600,
  padding: '7px 14px',
  borderRadius: '8px',
  background: '#1a1a1a',
  border: '1px solid #2a2a2a',
  color: '#ccc',
  transition: 'background 0.2s ease, color 0.2s ease',
  cursor: 'pointer',
  letterSpacing: '0.02em',
};